import { useEffect, useState } from "react";
import AppShell from "../../components/app/AppShell";
import AppEmptyState from "../../components/app/AppEmptyState";
import { FullPageLoading } from "../../components/Loading";
import {
  getProfile,
  listProfiles,
  updateSettings,
  type FullProfile,
} from "../../lib/api";

// Storyteller profile — sections read from /me/profile. The "Bearbeiten"
// button toggles inline edit-in-place for the two free-text fields the new
// /me/update-settings endpoint already accepts (sensitivities and
// chapters_to_exclude); the rest is read-only for now.

export default function AppStoryteller() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<FullProfile | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [sensitivities, setSensitivities] = useState("");
  const [excluded, setExcluded] = useState("");
  const [preferredAddress, setPreferredAddress] = useState("");
  const [hearingAbility, setHearingAbility] = useState("");
  const [dialectPreference, setDialectPreference] = useState("");
  const [livingSituation, setLivingSituation] = useState("");
  const [currentMoodNote, setCurrentMoodNote] = useState("");
  const [familyEventsNote, setFamilyEventsNote] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0];
        if (!p) {
          setLoading(false);
          return;
        }
        const detail = await getProfile(p.phone);
        setProfile(detail);
        setSensitivities(detail.sensitivities || "");
        setExcluded(detail.chapters_to_exclude || "");
        setPreferredAddress(detail.preferred_address || "");
        setHearingAbility(detail.hearing_ability || "");
        setDialectPreference(detail.dialect_preference || "");
        setLivingSituation(detail.living_situation || "");
        setCurrentMoodNote(detail.current_mood_note || "");
        setFamilyEventsNote(detail.family_events_note || "");
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Fehler beim Laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSave() {
    if (!profile) return;
    setSaving(true);
    try {
      const settings = {
        sensitivities,
        chapters_to_exclude: excluded,
        preferred_address: preferredAddress,
        hearing_ability: hearingAbility,
        dialect_preference: dialectPreference,
        living_situation: livingSituation,
        current_mood_note: currentMoodNote,
        family_events_note: familyEventsNote,
      };
      await updateSettings(profile.phone, settings);
      setProfile({ ...profile, ...settings });
      setEditMode(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Fehler beim Speichern.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <FullPageLoading />;

  if (!profile) {
    return (
      <AppShell
        eyebrow="Mein Buch"
        title={
          <>
            Über <em>den Erzähler</em>
          </>
        }
      >
        <AppEmptyState
          icon="phone"
          title="Noch kein Erzähler-Profil verknüpft."
          body="Hier sehen Sie alles zum Erzähler — Anrufnummer, Hörgewohnheiten, Vorlieben."
        />
      </AppShell>
    );
  }

  const firstName =
    profile?.interviewee_first_name ||
    profile?.interviewee_last_name ||
    "Erzählerin";
  const fullName = profile
    ? [profile.interviewee_first_name, profile.interviewee_last_name]
        .filter(Boolean)
        .join(" ")
    : "Erzählerin";
  const initials = profile
    ? [profile.interviewee_first_name, profile.interviewee_last_name]
        .map((s) => s?.[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
    : "—";
  const stamm = profile?.stammdaten || {};
  const interests = profile?.profileSummary || "";

  return (
    <AppShell
      eyebrow="Mein Buch"
      title={
        <>
          Profil <em>{firstName}</em>
        </>
      }
      action={
        editMode ? (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              background: "var(--oxblood)",
              color: "var(--paper-50)",
              border: "none",
              fontFamily: "var(--f-ui)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {saving ? "…" : "Speichern"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditMode(true)}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              background: "transparent",
              border: "1px solid rgba(43,26,15,0.20)",
              fontFamily: "var(--f-ui)",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--ink)",
              cursor: "pointer",
            }}
          >
            Bearbeiten
          </button>
        )
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(107,32,24,0.08)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderLeft: "3px solid var(--oxblood)",
              borderRadius: 4,
              padding: "10px 14px",
              color: "var(--oxblood-d)",
              fontFamily: "var(--f-body)",
              fontSize: 14,
              marginBottom: 18,
            }}
          >
            {error}
          </div>
        )}

        {/* Header card */}
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 14,
            padding: "20px 18px",
            textAlign: "center",
            marginBottom: 18,
          }}
        >
          <div
            className="oval-frame"
            style={{
              width: 96,
              height: 96,
              margin: "0 auto 10px",
              background: "var(--paper-200)",
              overflow: "hidden",
            }}
            aria-label={initials || undefined}
          >
            <img
              src="/images/hildegard-today.jpg"
              alt={fullName}
              className="photo-tone"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
          <div
            className="t-script"
            style={{ fontSize: 32, color: "var(--oxblood)", lineHeight: 1 }}
          >
            {fullName}
          </div>
          {(stamm.geburtsdatum || stamm.geburtsort) && (
            <div
              style={{
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 14,
                color: "var(--ink-faint)",
                marginTop: 4,
              }}
            >
              {[stamm.geburtsdatum, stamm.geburtsort].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>

        <ProfileSection title="Persönliches">
          <ProfileRow k="Geburtsdatum" v={stamm.geburtsdatum || "—"} />
          <ProfileRow k="Geburtsort" v={stamm.geburtsort || "—"} />
          <ProfileRow k="Heutiger Wohnort" v={stamm.wohnort || "—"} />
          <ProfileRow k="Sprache" v={stamm.sprache || "—"} last />
        </ProfileSection>

        <ProfileSection title="Anrufe">
          <ProfileRow k="Festnetz" v={profile?.phone || "—"} />
          <ProfileRow k="Tonfall" v={stamm.tonfall || "—"} />
          <ProfileRow k="Dialekt" v={stamm.dialekt || "—"} last />
        </ProfileSection>

        <ProfileSection title="Was sie gern erzählt">
          <div
            style={{
              padding: "14px 14px",
              fontFamily: "var(--f-body)",
              fontSize: 14.5,
              lineHeight: 1.5,
              color: "var(--ink)",
            }}
          >
            {interests ||
              "Noch keine Lieblingsthemen erfasst — die nächsten Anrufe bringen sie zutage."}
          </div>
        </ProfileSection>

        <ProfileSection title="Themen mit Vorsicht">
          {editMode ? (
            <div style={{ padding: "12px 14px" }}>
              <textarea
                value={sensitivities}
                onChange={(e) => setSensitivities(e.target.value)}
                rows={4}
                placeholder="Z. B. der Tod eines Angehörigen, Streit in der Familie"
                style={{
                  width: "100%",
                  background: "var(--paper-100)",
                  border: "1px solid rgba(43,26,15,0.14)",
                  borderRadius: 8,
                  padding: 10,
                  fontFamily: "var(--f-body)",
                  fontSize: 14,
                  color: "var(--ink)",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
          ) : (
            <div
              style={{
                padding: "14px 14px",
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 14,
                lineHeight: 1.5,
                color: "var(--ink-soft)",
                background: "rgba(168,122,58,0.10)",
                borderLeft: "3px solid var(--ochre)",
                margin: "6px 12px 12px",
                borderRadius: 4,
              }}
            >
              {sensitivities || "—"}
            </div>
          )}
        </ProfileSection>

        <ProfileSection title="Nicht behandelte Kapitel">
          {editMode ? (
            <div style={{ padding: "12px 14px" }}>
              <textarea
                value={excluded}
                onChange={(e) => setExcluded(e.target.value)}
                rows={3}
                placeholder="Kapitel, die nicht angesprochen werden sollen"
                style={{
                  width: "100%",
                  background: "var(--paper-100)",
                  border: "1px solid rgba(43,26,15,0.14)",
                  borderRadius: 8,
                  padding: 10,
                  fontFamily: "var(--f-body)",
                  fontSize: 14,
                  color: "var(--ink)",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
          ) : (
            <div
              style={{
                padding: "14px 14px",
                fontFamily: "var(--f-body)",
                fontSize: 14,
                color: "var(--ink)",
              }}
            >
              {excluded || "—"}
            </div>
          )}
        </ProfileSection>

        {/* Phone-AI warmth section — collected optionally on onboarding,
            always editable here. The orchestrator reads these on each
            session-prompt rebuild. */}
        <ProfileSection title="Für ein wärmeres Gespräch">
          {editMode ? (
            <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
              <WarmthField
                label={`Wie soll Konrad ${firstName} ansprechen?`}
                placeholder={firstName}
                value={preferredAddress}
                onChange={setPreferredAddress}
              />
              <div>
                <FieldLabel>Hörfähigkeit</FieldLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {(
                    [
                      ["", "Keine Angabe"],
                      ["gut", "Gut — normales Tempo"],
                      ["leicht_eingeschraenkt", "Leicht eingeschränkt"],
                      ["stark_eingeschraenkt", "Stark eingeschränkt — kurze Sätze"],
                    ] as const
                  ).map(([v, l]) => (
                    <label
                      key={v || "none"}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontFamily: "var(--f-body)",
                        fontSize: 13.5,
                        color: "var(--ink-soft)",
                      }}
                    >
                      <input
                        type="radio"
                        name="hearing-edit"
                        checked={hearingAbility === v}
                        onChange={() => setHearingAbility(v)}
                        style={{ accentColor: "var(--oxblood)" }}
                      />
                      {l}
                    </label>
                  ))}
                </div>
              </div>
              <WarmthField
                label="Sprach-/Dialektpräferenz"
                placeholder="z. B. Hochdeutsch, Plattdeutsch ok, Bayerisch"
                value={dialectPreference}
                onChange={setDialectPreference}
              />
              <WarmthField
                label="Lebenssituation"
                placeholder="z. B. alleinlebend, im Pflegeheim, mit Partner"
                value={livingSituation}
                onChange={setLivingSituation}
              />
              <WarmthField
                label="Aktuelle Stimmungs-/Lebenslage"
                placeholder="z. B. frisch verwitwet — Partner-Themen sensibel"
                value={currentMoodNote}
                onChange={setCurrentMoodNote}
                rows={2}
              />
              <WarmthField
                label="Aktuelle Familien-Ereignisse"
                placeholder="z. B. neuer Urenkel Leo seit März"
                value={familyEventsNote}
                onChange={setFamilyEventsNote}
                rows={2}
              />
            </div>
          ) : (
            <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
              <WarmthRow label="Anrede" value={preferredAddress} />
              <WarmthRow
                label="Hörfähigkeit"
                value={
                  hearingAbility === "gut"
                    ? "Gut — normales Tempo"
                    : hearingAbility === "leicht_eingeschraenkt"
                    ? "Leicht eingeschränkt"
                    : hearingAbility === "stark_eingeschraenkt"
                    ? "Stark eingeschränkt"
                    : ""
                }
              />
              <WarmthRow label="Dialekt" value={dialectPreference} />
              <WarmthRow label="Lebenssituation" value={livingSituation} />
              <WarmthRow label="Stimmungslage" value={currentMoodNote} />
              <WarmthRow label="Familien-Ereignisse" value={familyEventsNote} />
            </div>
          )}
        </ProfileSection>
      </div>
    </AppShell>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="t-caps"
      style={{
        color: "var(--ink-faint)",
        marginBottom: 6,
        fontSize: 11,
      }}
    >
      {children}
    </div>
  );
}

function WarmthField({
  label,
  value,
  onChange,
  placeholder,
  rows,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  const sharedStyle: React.CSSProperties = {
    width: "100%",
    background: "var(--paper-100)",
    border: "1px solid rgba(43,26,15,0.14)",
    borderRadius: 8,
    padding: 10,
    fontFamily: "var(--f-body)",
    fontSize: 14,
    color: "var(--ink)",
    boxSizing: "border-box",
  };
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      {rows && rows > 1 ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          style={{ ...sharedStyle, resize: "vertical" }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={sharedStyle}
        />
      )}
    </div>
  );
}

function WarmthRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        fontFamily: "var(--f-body)",
        fontSize: 14,
      }}
    >
      <span
        className="t-caps"
        style={{ color: "var(--ink-faint)", flexShrink: 0 }}
      >
        {label}
      </span>
      <span
        style={{
          color: value ? "var(--ink)" : "var(--ink-faint)",
          textAlign: "right",
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function ProfileSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="t-caps" style={{ marginBottom: 8 }}>
        {title}
      </div>
      <div
        style={{
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.10)",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ProfileRow({
  k,
  v,
  last,
}: {
  k: string;
  v: string;
  last?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "12px 16px",
        borderBottom: last ? "none" : "1px solid rgba(43,26,15,0.08)",
        gap: 12,
      }}
    >
      <div
        style={{
          fontFamily: "var(--f-ui)",
          fontSize: 12,
          color: "var(--ink-faint)",
          textTransform: "uppercase",
          letterSpacing: "0.10em",
        }}
      >
        {k}
      </div>
      <div
        style={{
          fontFamily: "var(--f-display)",
          fontSize: 15,
          color: "var(--ink)",
          textAlign: "right",
          maxWidth: "60%",
        }}
      >
        {v}
      </div>
    </div>
  );
}
